import React, { useState, useEffect, useCallback, useRef } from 'react';
import ColorThief from 'color-thief-browser';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useOnboarding } from '../context/OnboardingContext';
import { 
  Settings as SettingsIcon,
  User,
  Lock,
  Bell,
  Globe,
  Palette,
  Shield,
  Trash2,
  Save,
  Camera,
  Mail,
  Phone,
  MapPin,
  Eye,
  EyeOff,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader,
  AlertCircle,
  X,
  Sun,
  Moon,
  RotateCcw,
  Users,
  Settings2
} from 'lucide-react';
import profileService from '../services/profileService';
import teamService from '../services/teamService';
import TeamPage from './TeamPage';
import offersService from '../services/offersService';
import { locationService } from '../services';
import PermissionGate from '../components/PermissionGate';
import './SettingsPage.css';

const LANDING_THEME_OPTIONS = [
  {
    value: 'neutral',
    label: 'صنوبري',
    desc: 'خلفية فاتحة، بطاقات بيضاء، أزرار خضراء داكنة'
  },
  {
    value: 'classic',
    label: 'البني والذهبي',
    desc: 'خلفية دافئة، أزرار بنية، لمسات ذهبية'
  },
  {
    value: 'custom',
    label: 'من الشعار',
    desc: 'قريبًا',
    disabled: true
  }
];

const HERO_IMAGE_MODE_OPTIONS = [
  { value: 'default', label: 'استخدام الصورة الافتراضية', desc: 'تستخدم صورة الغلاف الافتراضية الحالية.' },
  { value: 'custom', label: 'رفع صورة خاصة', desc: 'ارفع صورة تظهر في أعلى صفحتك العقارية.' },
  { value: 'none', label: 'إزالة صورة الغلاف', desc: 'تظهر خلفية تدرج نظيفة من الثيم الحالي بدل الصورة.' }
];

const PUBLIC_SITE_ORIGIN = 'https://miskest.com';

const PREDEFINED_SERVICES = [
  'بيع عقارات',
  'تأجير عقارات',
  'تسويق عقاري',
  'توثيق عقود',
  'استشارات عقارية',
  'إدارة أملاك',
  'إفراغ عقاري',
  'تقييم عقاري',
  'تصوير عقاري',
  'إدارة مزادات'
];

const splitChipText = (value) => (
  typeof value === 'string'
    ? value.split(/[،,·|]+/).map(item => item.trim()).filter(Boolean)
    : []
);

const toggleListValue = (list, value) => (
  list.includes(value) ? list.filter(item => item !== value) : [...list, value]
);

const joinChipText = (list) => list.join(' · ');

const isValidHexColor = (color) => (
  typeof color === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color.trim())
);

const getReadableThemeColor = (color) => {
  if (!isValidHexColor(color)) return '#14532D';
  const hex = color.length === 4
    ? color.slice(1).split('').map(part => part + part).join('')
    : color.slice(1);
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  if (luminance <= 0.48) return color;
  const darken = value => Math.max(0, Math.round(value * 0.58));
  return `#${[darken(red), darken(green), darken(blue)].map(value => value.toString(16).padStart(2, '0')).join('')}`;
};

const getThemePreviewColors = (theme, brandColor) => {
  if (theme === 'classic') {
    return {
      page: '#F5F0E8',
      top: '#2C1810',
      card: '#FFFFFF',
      border: '#E5DED4',
      text: '#2C1810',
      muted: '#8B735F',
      primary: '#2C1810',
      accent: '#C5A880'
    };
  }

  if (theme === 'custom' && isValidHexColor(brandColor)) {
    const readableBrandColor = getReadableThemeColor(brandColor);
    return {
      page: '#F8FAFC',
      top: '#FFFFFF',
      card: '#FFFFFF',
      border: '#E5E7EB',
      text: '#111827',
      muted: '#6B7280',
      primary: readableBrandColor,
      accent: brandColor
    };
  }

  return {
    page: '#F8FAFC',
    top: '#14532D',
    card: '#FFFFFF',
    border: '#E5E7EB',
    text: '#111827',
    muted: '#6B7280',
    primary: '#14532D',
    accent: '#14532D'
  };
};

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const { user, updateProfile, logout, refreshUser } = useAuth();
  const isSubAccount = user?.accountType === 'sub';
  const { theme, setTheme } = useTheme();
  const { resetOnboarding, startOnboarding } = useOnboarding();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRTL = i18n.language === 'ar';
  const fileInputRef = useRef(null);
  
  // Get tab from URL params or default to 'profile'
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  const [showMobileSettingsMenu, setShowMobileSettingsMenu] = useState(!tabFromUrl);
  const [showPassword, setShowPassword] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [deletedOffers, setDeletedOffers] = useState([]);
  const [deletedOffersLoading, setDeletedOffersLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'other') return;
    setDeletedOffersLoading(true);
    offersService.getDeletedOffers()
      .then(res => setDeletedOffers(res.data?.offers || res.data || []))
      .catch(() => setDeletedOffers([]))
      .finally(() => setDeletedOffersLoading(false));
  }, [activeTab]);

  const handleRestoreOffer = async (id) => {
    await offersService.restoreOffer(id);
    setDeletedOffersLoading(true);
    offersService.getDeletedOffers()
      .then(res => setDeletedOffers(res.data?.offers || res.data || []))
      .catch(() => setDeletedOffers([]))
      .finally(() => setDeletedOffersLoading(false));
  };

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    publicContactPhone: '',
    publicWhatsappPhone: '',
    publicContactEmail: '',
    city: '',
    bio: '',
    heroIntroText: '',
    avatar: '',
    heroImageUrl: '',
    username: '',
    specialty: '',
    experience: '',
    falLicense: '',
    dealsDone: '',
    languages: '',
    priceRangeMin: '',
    priceRangeMax: '',
    districts: '',
    twitter: '',
    instagram: '',
    tiktok: '',
    extraLink: '',
    whatsappGroupUrl: '',
    whatsappGroupLabel: '',
    additionalContactNumbers: [],
    brandColor: '#C5A880',
    landingTheme: 'neutral',
    heroImageMode: 'default',
    showHeroTrustLine: true,
    showAboutSection: true,
    showStatsSection: true,
    showSpecialtySection: true,
    showCatalogsSection: true,
    showOffersSection: true,
    showTestimonialsSection: true,
    showContactSection: true,
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [notifications, setNotifications] = useState({
    newMatches: true,
    newRequests: true,
    networkActivity: true,
    messages: true,
    marketing: false,
    emailNotifications: true,
    pushNotifications: true
  });

  const [privacy, setPrivacy] = useState({
    showProfile: true,
    showPhone: false
  });

  const [preferences, setPreferences] = useState({
    language: i18n.language,
    theme: 'light'
  });

  // Hero image + services state (landing tab)
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImageMode, setHeroImageMode] = useState('default');
  const [services, setServices] = useState(profile.services || []);
  const [newService, setNewService] = useState('');
  const [selectedDistrictCities, setSelectedDistrictCities] = useState([]);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showInstagramInput, setShowInstagramInput] = useState(false);
  const [showTiktokInput, setShowTiktokInput] = useState(false);
  const [showTwitterInput, setShowTwitterInput] = useState(false);
  const [showExtraLinkInput, setShowExtraLinkInput] = useState(false);
  const [showWhatsappGroupInput, setShowWhatsappGroupInput] = useState(false);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const heroFileInputRef = useRef(null);

  // Avatar color extraction state for the public logo/account image.
  const [extractedColors, setExtractedColors] = useState([]);
  const [themeDraft, setThemeDraft] = useState({
    landingTheme: 'neutral',
    brandColor: '#C5A880'
  });

  const getLandingUsername = useCallback(() => {
    const directUsername = (
      profile.username ||
      profile.profile?.username ||
      user?.username ||
      user?.profile?.username ||
      user?.data?.username ||
      user?.user?.username
    );
    if (String(directUsername || '').trim()) return String(directUsername).trim();

    try {
      const stored = JSON.parse(localStorage.getItem('msk_user_data') || '{}');
      return String(stored?.username || stored?.profile?.username || '').trim();
    } catch (_) {
      return '';
    }
  }, [profile, user]);

  const landingUsername = getLandingUsername();
  const landingPath = landingUsername ? `/p/${encodeURIComponent(landingUsername)}` : '';
  const landingFullUrl = landingPath ? `${PUBLIC_SITE_ORIGIN}${landingPath}` : '';

  const handleCopyLandingUrl = useCallback(async () => {
    if (!landingFullUrl) {
      setError('أضف اسم مستخدم لإنشاء رابط موقعك العقاري.');
      return;
    }

    try {
      await navigator.clipboard.writeText(landingFullUrl);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (_) {
      setError('تعذر نسخ الرابط. انسخه يدوياً من الحقل.');
    }
  }, [landingFullUrl]);

  const handlePreviewLandingUrl = useCallback(() => {
    if (!landingPath) {
      setError('أضف اسم مستخدم لإنشاء رابط موقعك العقاري.');
      return;
    }

    window.open(landingPath, '_blank', 'noopener,noreferrer');
  }, [landingPath]);

  // Pending team invite state
  const [pendingInvite, setPendingInvite] = useState(null);
  const [inviteActionLoading, setInviteActionLoading] = useState(false);
  const [inviteActionError, setInviteActionError] = useState('');

  // Fetch profile and settings from API
  const fetchProfileAndSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [profileResponse, settingsResponse] = await Promise.all([
        profileService.getProfile(),
        profileService.getSettings()
      ]);
      
      if (profileResponse.success) {
        const userData = profileResponse.data.user || profileResponse.data;
        setProfile({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          // Undefined legacy fields borrow account values only until the first landing save.
          publicContactPhone: userData.publicContactPhone === undefined ? (userData.phone || '') : userData.publicContactPhone,
          publicWhatsappPhone: userData.publicWhatsappPhone === undefined ? (userData.phone || '') : userData.publicWhatsappPhone,
          publicContactEmail: userData.publicContactEmail === undefined ? (userData.email || '') : userData.publicContactEmail,
          city: userData.city || '',
          bio: userData.bio || '',
          heroIntroText: userData.heroIntroText || '',
          avatar: userData.avatarUrl || userData.avatar || '',
          heroImageUrl: userData.heroImageUrl || '',
          username: userData.username || '',
          specialty: userData.specialty || '',
          experience: userData.experience || '',
          falLicense: userData.falLicense || '',
          dealsDone: userData.dealsDone || '',
          languages: userData.languages || '',
          priceRangeMin: userData.priceRangeMin || '',
          priceRangeMax: userData.priceRangeMax || '',
          districts: userData.districts || '',
          twitter: userData.twitter || '',
          instagram: userData.instagram || '',
          tiktok: userData.tiktok || '',
          extraLink: userData.extraLink || '',
          whatsappGroupUrl: userData.whatsappGroupUrl || '',
          whatsappGroupLabel: userData.whatsappGroupLabel || '',
          // Extra contacts are edited from the landing contact card only.
          additionalContactNumbers: Array.isArray(userData.additionalContactNumbers) ? userData.additionalContactNumbers : [],
          brandColor: userData.brandColor || '#C5A880',
          landingTheme: userData.landingTheme === 'custom' ? 'neutral' : (userData.landingTheme || 'neutral'),
          heroImageMode: userData.heroImageMode || 'default',
          showHeroTrustLine: userData.showHeroTrustLine !== false,
          showAboutSection: userData.showAboutSection !== false,
          showStatsSection: userData.showStatsSection !== false,
          showSpecialtySection: userData.showSpecialtySection !== false,
          showCatalogsSection: userData.showCatalogsSection !== false,
          showOffersSection: userData.showOffersSection !== false,
          showTestimonialsSection: userData.showTestimonialsSection !== false,
          showContactSection: userData.showContactSection !== false,
        });
        setThemeDraft({
          landingTheme: userData.landingTheme === 'custom' ? 'neutral' : (userData.landingTheme || 'neutral'),
          brandColor: userData.brandColor || '#C5A880'
        });
        setHeroImageMode(userData.heroImageMode || 'default');
        setHeroImagePreview(userData.heroImageUrl || null);
        setServices(Array.isArray(userData.services) ? userData.services : []);
        setSelectedDistrictCities(splitChipText(userData.districts || ''));
        setShowInstagramInput(Boolean(userData.instagram));
        setShowTiktokInput(Boolean(userData.tiktok));
        setShowTwitterInput(Boolean(userData.twitter));
        setShowExtraLinkInput(Boolean(userData.extraLink));
        setShowWhatsappGroupInput(Boolean(userData.whatsappGroupUrl));
      }
      
      if (settingsResponse.success) {
        const data = settingsResponse.data;
        if (data.notificationSettings) {
          setNotifications(data.notificationSettings);
        }
        if (data.privacySettings) {
          setPrivacy(data.privacySettings);
        }
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch cities from API
  const fetchCities = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await locationService.getAllCities(currentLang);
      if (response.success && response.data.cities) {
        const citiesData = response.data.cities;
        setCities(citiesData);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingLocations(false);
    }
  }, [currentLang]);

  useEffect(() => {
    fetchProfileAndSettings();
    fetchCities();
    // Check for pending team invite
    teamService.getPendingInvite()
      .then(res => {
        if (res?.success && res?.data?.hasPendingInvite) {
          setPendingInvite(res.data);
        }
      })
      .catch(() => {});
  }, [fetchProfileAndSettings, fetchCities]);

  const tabs = [
    { id: 'profile', label: 'بيانات الحساب', icon: <User size={20} /> },
    { id: 'security', label: t('settings.security'), icon: <Lock size={20} /> },
    { id: 'notifications', label: t('settings.notifications'), icon: <Bell size={20} /> },
    { id: 'preferences', label: t('settings.preferences'), icon: <Palette size={20} /> },
    { id: 'privacy', label: t('settings.privacy'), icon: <Shield size={20} /> },
    { id: 'landing', label: 'موقعي العقاري', icon: <Globe size={18} /> },
    { id: 'other', label: 'أخرى', icon: <Settings2 size={20} /> },
    ...(!isSubAccount ? [{ id: 'team', label: 'فريقي', icon: <Users size={20} /> }] : []),
  ];

  const validTabIds = React.useMemo(() => (
    ['profile', 'security', 'notifications', 'preferences', 'privacy', 'landing', 'other', ...(!isSubAccount ? ['team'] : [])]
  ), [isSubAccount]);

  useEffect(() => {
    // Keep the selected settings tab in sync when the URL query changes.
    const nextTab = tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : 'profile';
    if (activeTab !== nextTab) {
      setActiveTab(nextTab);
    }
    // On mobile, direct tab links open the tab content; no-tab links show the settings menu first.
    setShowMobileSettingsMenu(!tabFromUrl);
  }, [tabFromUrl, validTabIds, activeTab]);

  const handleTabChange = (tabId) => {
    // Store the active tab in the URL so direct links like /settings?tab=landing keep working.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tabId);
    setActiveTab(tabId);
    setShowMobileSettingsMenu(false);
    setSearchParams(nextParams);
  };

  const handleBackToSettingsMenu = () => {
    setShowMobileSettingsMenu(true);
  };

  const handleSaveProfile = async () => {
    setError(null);
    setSaving(true);
    
    try {
      // Profile tab saves only basic account data; landing bio is saved from the landing tab.
      const response = await profileService.updateProfile({
        name: profile.name,
        phone: profile.phone,
        city: profile.city
      });
      if (response.success) {
        updateProfile(response.data.user || response.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setError(null);
    
    if (passwords.new !== passwords.confirm) {
      setError(t('errors.passwordMismatch', 'كلمات المرور غير متطابقة'));
      return;
    }
    
    if (passwords.new.length < 6) {
      setError(t('errors.passwordTooShort', 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'));
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await profileService.changePassword({
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
      
      if (response.success) {
        setPasswords({ current: '', new: '', confirm: '' });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeLanguage = (lang) => {
    setPreferences({ ...preferences, language: lang });
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    localStorage.setItem('language', lang);
    // Save to backend
    profileService.updatePreferences({ language: lang }).catch(console.error);
  };

  const extractColorsFromImageFile = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const colorThief = new ColorThief();
        const palette = colorThief.getPalette(img, 5);
        const hexColors = palette.map(([r, g, b]) => (
          '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')
        ));
        setExtractedColors(hexColors);
      } catch (err) {
        console.error('Color extraction failed:', err);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    img.src = objectUrl;
  };

  // Handle avatar upload from the landing tab logo/account image section.
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('يرجى اختيار صورة صالحة');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setAvatarUploading(true);
    setError(null);
    extractColorsFromImageFile(file);

    try {
      const response = await profileService.uploadAvatar(file);
      if (response.success) {
        const userData = response.data.user || response.data;
        setProfile(prev => ({ ...prev, avatar: userData.avatarUrl || userData.avatar }));
        updateProfile(userData);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || 'فشل في رفع الصورة');
      }
    } catch (err) {
      setError(err.message || 'فشل في رفع الصورة');
    } finally {
      setAvatarUploading(false);
    }
  };

  // Handle notification settings save
  const handleSaveNotifications = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await profileService.updateNotificationSettings(notifications);
      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle privacy settings save
  const handleSavePrivacy = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await profileService.updatePrivacySettings(privacy);
      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle theme change
  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    setPreferences(prev => ({ ...prev, theme: newTheme }));
    // Save to backend
    try {
      await profileService.updatePreferences({ theme: newTheme });
    } catch (err) {
      console.error('Error saving theme:', err);
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('يرجى إدخال كلمة المرور');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const response = await profileService.deleteAccount(deletePassword);
      if (response.success) {
        // Logout and redirect
        logout();
      } else {
        setError(response.message || 'فشل في حذف الحساب');
      }
    } catch (err) {
      setError(err.message || 'فشل في حذف الحساب');
    } finally {
      setDeleting(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!pendingInvite?.token) return;
    setInviteActionLoading(true);
    setInviteActionError('');
    try {
      const res = await teamService.acceptInvite(pendingInvite.token);
      if (res.success) {
        setPendingInvite(null);
        // Update localStorage so accountType reflects 'sub' immediately
        refreshUser();
      } else {
        setInviteActionError(res.message || 'فشل قبول الدعوة، حاول مرة أخرى');
      }
    } catch {
      setInviteActionError('حدث خطأ في الاتصال، حاول مرة أخرى');
    } finally {
      setInviteActionLoading(false);
    }
  };

  const handleRejectInvite = async () => {
    setInviteActionLoading(true);
    setInviteActionError('');
    try {
      const res = await teamService.rejectInvite();
      if (res.success) {
        setPendingInvite(null);
      } else {
        setInviteActionError(res.message || 'فشل رفض الدعوة، حاول مرة أخرى');
      }
    } catch {
      setInviteActionError('حدث خطأ في الاتصال، حاول مرة أخرى');
    } finally {
      setInviteActionLoading(false);
    }
  };

  const handleThemeSelect = (landingTheme) => {
    // The logo-derived theme is paused, so visitors can only pick the two stable themes.
    if (landingTheme === 'custom') return;
    setThemeDraft(prev => ({
      ...prev,
      landingTheme
    }));
  };

  const handleExtractedColorSelect = (hex) => {
    // Color picking stays disabled while the logo-derived theme is paused.
    if (themeDraft.landingTheme !== 'custom') return;
    setThemeDraft({
      landingTheme: 'custom',
      brandColor: hex
    });
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('يرجى اختيار صورة صالحة');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }
    setHeroImagePreview(URL.createObjectURL(file));
    setHeroImageFile(file);
    setHeroImageMode('custom');
  };

  const handleHeroImageModeChange = (mode) => {
    setHeroImageMode(mode);
    // Keep the saved cover preview available when switching back to custom.
    if (mode !== 'custom') {
      setHeroImageFile(null);
      setHeroImagePreview(profile.heroImageUrl || null);
    }
  };

  const handleDistrictCityToggle = (cityName) => {
    const nextCities = toggleListValue(selectedDistrictCities, cityName);
    setSelectedDistrictCities(nextCities);
    // Keep using the old districts field, but fill it with selected city names.
    setProfile({ ...profile, districts: joinChipText(nextCities) });
  };

  const handleServiceToggle = (serviceName) => {
    setServices(toggleListValue(services, serviceName));
  };

  const handleResetServices = () => {
    setServices([]);
    setNewService('');
    setServiceDropdownOpen(false);
  };

  const createEmptyAdditionalContact = () => ({
    // Defaults make a new contact useful on the public site right away.
    label: '',
    phone: '',
    whatsappEnabled: true,
    callEnabled: false,
    showInPublicSite: true,
    showInCatalog: false
  });

  const handleAddAdditionalContact = () => {
    setProfile(prev => ({
      ...prev,
      additionalContactNumbers: [
        ...(Array.isArray(prev.additionalContactNumbers) ? prev.additionalContactNumbers : []),
        createEmptyAdditionalContact()
      ]
    }));
  };

  const handleUpdateAdditionalContact = (index, field, value) => {
    setProfile(prev => ({
      ...prev,
      additionalContactNumbers: (Array.isArray(prev.additionalContactNumbers) ? prev.additionalContactNumbers : []).map((contact, contactIndex) => (
        contactIndex === index ? { ...contact, [field]: value } : contact
      ))
    }));
  };

  const handleRemoveAdditionalContact = (index) => {
    setProfile(prev => ({
      ...prev,
      additionalContactNumbers: (Array.isArray(prev.additionalContactNumbers) ? prev.additionalContactNumbers : []).filter((_, contactIndex) => contactIndex !== index)
    }));
  };

  const handleSaveLanding = async () => {
    setSaving(true);
    setError(null);
    try {
      const submittedUsername = String(profile.username || '').trim();
      if (heroImageFile) {
        const uploadResponse = await profileService.uploadHeroImage(heroImageFile);
        const uploadedUser = uploadResponse?.data?.user || uploadResponse?.data;
        if (uploadedUser?.heroImageUrl) {
          setHeroImagePreview(uploadedUser.heroImageUrl);
          setProfile(prev => ({ ...prev, heroImageUrl: uploadedUser.heroImageUrl }));
        }
        setHeroImageFile(null);
      }
      const response = await profileService.updateProfile({
        services,
        username: submittedUsername,
        specialty: profile.specialty,
        experience: profile.experience,
        dealsDone: profile.dealsDone,
        falLicense: profile.falLicense,
        bio: profile.bio,
        heroIntroText: profile.heroIntroText,
        districts: profile.districts,
        heroImageMode,
        landingTheme: themeDraft.landingTheme,
        brandColor: themeDraft.landingTheme === 'custom' ? themeDraft.brandColor : profile.brandColor,
        priceRangeMin: profile.priceRangeMin,
        priceRangeMax: profile.priceRangeMax,
        languages: profile.languages,
        twitter: profile.twitter,
        instagram: profile.instagram,
        tiktok: profile.tiktok,
        extraLink: profile.extraLink,
        whatsappGroupUrl: profile.whatsappGroupUrl,
        whatsappGroupLabel: profile.whatsappGroupLabel,
        additionalContactNumbers: Array.isArray(profile.additionalContactNumbers) ? profile.additionalContactNumbers : [],
        // These public values remain independent, including deliberately saved empty strings.
        publicContactPhone: profile.publicContactPhone,
        publicWhatsappPhone: profile.publicWhatsappPhone,
        publicContactEmail: profile.publicContactEmail,
        // This toggle controls only the trust badges in the public hero.
        showHeroTrustLine: profile.showHeroTrustLine,
        showAboutSection: profile.showAboutSection,
        showStatsSection: profile.showStatsSection,
        showSpecialtySection: profile.showSpecialtySection,
        showCatalogsSection: profile.showCatalogsSection,
        showOffersSection: profile.showOffersSection,
        showTestimonialsSection: profile.showTestimonialsSection,
        showContactSection: profile.showContactSection,
      });
      if (response.success) {
        const updatedUser = response.data.user || response.data;
        const mergedUser = {
          ...updatedUser,
          username: updatedUser?.username || submittedUsername
        };
        updateProfile(mergedUser);
        setProfile(prev => ({
          ...prev,
          username: mergedUser.username || prev.username,
          heroImageUrl: updatedUser.heroImageUrl || prev.heroImageUrl,
          heroImageMode: updatedUser.heroImageMode || heroImageMode,
          landingTheme: updatedUser.landingTheme || themeDraft.landingTheme,
          brandColor: updatedUser.brandColor || prev.brandColor,
          bio: updatedUser.bio !== undefined ? updatedUser.bio : prev.bio,
          heroIntroText: updatedUser.heroIntroText !== undefined ? updatedUser.heroIntroText : prev.heroIntroText,
          whatsappGroupUrl: updatedUser.whatsappGroupUrl !== undefined ? updatedUser.whatsappGroupUrl : prev.whatsappGroupUrl,
          whatsappGroupLabel: updatedUser.whatsappGroupLabel !== undefined ? updatedUser.whatsappGroupLabel : prev.whatsappGroupLabel,
          additionalContactNumbers: Array.isArray(updatedUser.additionalContactNumbers) ? updatedUser.additionalContactNumbers : prev.additionalContactNumbers,
          // Keep the toggle state fresh after saving so the UI matches the backend.
          showHeroTrustLine: updatedUser.showHeroTrustLine !== false
        }));
        setHeroImageMode(updatedUser.heroImageMode || heroImageMode);
        setThemeDraft(prev => ({
          ...prev,
          landingTheme: updatedUser.landingTheme || themeDraft.landingTheme,
          brandColor: updatedUser.brandColor || prev.brandColor
        }));
        await refreshUser();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loader className="spin" size={40} />
        </div>
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_SETTINGS" type="page">
    <div className="settings-page">
      <div className="page-header">
        <div className="header-title">
          <SettingsIcon size={28} />
          <h1>{t('settings.title')}</h1>
        </div>
      </div>

      {pendingInvite && (
        <div className="invite-banner" dir="rtl">
          <div className="invite-banner-content">
            <span className="invite-banner-icon">👥</span>
            <span className="invite-banner-text">
              دعاك <strong>{pendingInvite.inviterName || 'أحد المستخدمين'}</strong> للانضمام لفريقه
            </span>
          </div>
          <div className="invite-banner-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAcceptInvite}
              disabled={inviteActionLoading}
            >
              {inviteActionLoading ? <Loader size={14} className="spin" /> : 'قبول'}
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={handleRejectInvite}
              disabled={inviteActionLoading}
            >
              رفض
            </button>
          </div>
          {inviteActionError && (
            <div className="invite-banner-error">{inviteActionError}</div>
          )}
        </div>
      )}

      <div className={`settings-container${showMobileSettingsMenu ? ' mobile-menu-open' : ' mobile-tab-open'}`}>
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button 
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
                {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </button>
            ))}
          </nav>
        </div>

        <div className="settings-content">
          <button
            type="button"
            className="mobile-settings-back"
            onClick={handleBackToSettingsMenu}
          >
            <ChevronRight size={18} />
            رجوع للإعدادات
          </button>
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>{t('settings.profile')}</h2>
              <p className="section-desc">قم بتحديث معلومات ملفك الشخصي</p>

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    <User size={16} />
                    {t('settings.name')}
                  </label>
                  <input 
                    type="text"
                    className="form-control"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <Mail size={16} />
                    {t('settings.email')}
                  </label>
                  <input 
                    type="email"
                    className="form-control"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <Phone size={16} />
                    {t('settings.phone')}
                  </label>
                  <input 
                    type="tel"
                    className="form-control"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <MapPin size={16} />
                    المدينة
                  </label>
                  <select 
                    className="form-control"
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                    disabled={loadingLocations}
                  >
                    <option value="">{loadingLocations ? 'جاري التحميل...' : 'اختر المدينة'}</option>
                    {cities.map(city => (
                      <option key={city._id} value={city.label}>{city.label}</option>
                    ))}
                  </select>
                </div>

              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving || avatarUploading}>
                  {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                  {t('common.save')}
                </button>
                {saveSuccess && (
                  <span className="save-success">
                    <Check size={18} />
                    تم الحفظ بنجاح
                  </span>
                )}
                {error && activeTab === 'profile' && (
                  <span className="error-message">
                    <AlertCircle size={18} />
                    {error}
                  </span>
                )}
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>{t('settings.security')}</h2>
              <p className="section-desc">قم بتحديث كلمة المرور وإعدادات الأمان</p>

              {error && activeTab === 'security' && (
                <div className="error-banner">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <div className="form-grid">
                <div className="form-group full-width">
                  <label>{t('settings.currentPassword')}</label>
                  <div className="password-input">
                    <input 
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    />
                    <button 
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('settings.newPassword')}</label>
                  <input 
                    type="password"
                    className="form-control"
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>{t('settings.confirmPassword')}</label>
                  <input 
                    type="password"
                    className="form-control"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleChangePassword} disabled={saving}>
                  {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                  تحديث كلمة المرور
                </button>
                {saveSuccess && activeTab === 'security' && (
                  <span className="save-success">
                    <Check size={18} />
                    تم التحديث بنجاح
                  </span>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>{t('settings.notifications')}</h2>
              <p className="section-desc">تحكم في الإشعارات التي تصلك</p>

              <div className="notification-options">
                <div className="notification-item">
                  <div>
                    <h4>تطابقات جديدة</h4>
                    <p>إشعار عند وجود تطابق بين عروضك والطلبات</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={notifications.newMatches}
                      onChange={(e) => setNotifications({ ...notifications, newMatches: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div>
                    <h4>طلبات جديدة</h4>
                    <p>إشعار عند استلام طلب جديد من العملاء</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={notifications.newRequests}
                      onChange={(e) => setNotifications({ ...notifications, newRequests: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div>
                    <h4>نشاط الشبكة</h4>
                    <p>إشعار عند التفاعل مع عروضك في الشبكة</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={notifications.networkActivity}
                      onChange={(e) => setNotifications({ ...notifications, networkActivity: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div>
                    <h4>الرسائل</h4>
                    <p>إشعار عند استلام رسالة جديدة</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={notifications.messages}
                      onChange={(e) => setNotifications({ ...notifications, messages: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div>
                    <h4>العروض التسويقية</h4>
                    <p>استلم آخر العروض والتحديثات من منصة مسك</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={notifications.marketing}
                      onChange={(e) => setNotifications({ ...notifications, marketing: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveNotifications} disabled={saving}>
                  {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                  حفظ الإعدادات
                </button>
                {saveSuccess && activeTab === 'notifications' && (
                  <span className="save-success">
                    <Check size={18} />
                    تم الحفظ بنجاح
                  </span>
                )}
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h2>{t('settings.preferences')}</h2>
              <p className="section-desc">تخصيص تجربة استخدامك للمنصة</p>

              <div className="preference-options">
                <div className="preference-item">
                  <div>
                    <h4>
                      <Globe size={18} />
                      {t('settings.language')}
                    </h4>
                    <p>اختر لغة واجهة المنصة</p>
                  </div>
                  <div className="language-options">
                    <button 
                      className={`lang-btn ${preferences.language === 'ar' ? 'active' : ''}`}
                      onClick={() => handleChangeLanguage('ar')}
                    >
                      العربية
                    </button>
                    <button 
                      className={`lang-btn ${preferences.language === 'en' ? 'active' : ''}`}
                      onClick={() => handleChangeLanguage('en')}
                    >
                      English
                    </button>
                  </div>
                </div>

                <div className="preference-item">
                  <div>
                    <h4>
                      <Palette size={18} />
                      المظهر
                    </h4>
                    <p>اختر مظهر واجهة المنصة</p>
                  </div>
                  <div className="theme-options">
                    <button 
                      className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                      onClick={() => handleThemeChange('light')}
                    >
                      <Sun size={18} />
                      فاتح
                    </button>
                    <button 
                      className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                      onClick={() => handleThemeChange('dark')}
                    >
                      <Moon size={18} />
                      داكن
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="settings-section">
              <h2>{t('settings.privacy')}</h2>
              <p className="section-desc">تحكم في خصوصية حسابك</p>

              <div className="privacy-options">
                <div className="notification-item">
                  <div>
                    <h4>إظهار الملف الشخصي</h4>
                    <p>السماح للمسوقين الآخرين برؤية ملفك</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={privacy.showProfile}
                      onChange={(e) => setPrivacy({ ...privacy, showProfile: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div>
                    <h4>إظهار رقم الهاتف</h4>
                    <p>السماح للآخرين برؤية رقم هاتفك</p>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox"
                      checked={privacy.showPhone}
                      onChange={(e) => setPrivacy({ ...privacy, showPhone: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSavePrivacy} disabled={saving}>
                  {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                  حفظ إعدادات الخصوصية
                </button>
                {saveSuccess && activeTab === 'privacy' && (
                  <span className="save-success">
                    <Check size={18} />
                    تم الحفظ بنجاح
                  </span>
                )}
              </div>

              <div className="settings-section" style={{ marginBottom: '24px' }}>
                <div className="danger-item" style={{ borderColor: 'var(--border-color)' }}>
                  <div>
                    <h4>{t('onboarding.restartTour')}</h4>
                    <p>{t('onboarding.welcome.description')}</p>
                  </div>
                  <button className="btn btn-outline" onClick={() => { resetOnboarding(); startOnboarding(); }}>
                    <RotateCcw size={18} />
                    {t('onboarding.restartTour')}
                  </button>
                </div>
              </div>

              <div className="danger-zone">
                <h3>منطقة الخطر</h3>
                <div className="danger-item">
                  <div>
                    <h4>حذف الحساب</h4>
                    <p>سيتم حذف جميع بياناتك بشكل نهائي</p>
                  </div>
                  <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
                    <Trash2 size={18} />
                    حذف الحساب
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'landing' && (() => {
            const availablePropertiesVisible = profile.showCatalogsSection && profile.showOffersSection;
            const cityOptions = cities
              .map(city => city.arLabel || city.label || city.value)
              .filter(Boolean);
            const filteredCityOptions = cityOptions.filter(cityName =>
              cityName.toLowerCase().includes(citySearch.trim().toLowerCase())
            );
            const availableContactOptions = [
              { key: 'instagram', label: 'إنستغرام', show: showInstagramInput, onSelect: () => setShowInstagramInput(true) },
              { key: 'tiktok', label: 'تيك توك', show: showTiktokInput, onSelect: () => setShowTiktokInput(true) },
              { key: 'twitter', label: 'إكس', show: showTwitterInput, onSelect: () => setShowTwitterInput(true) },
              { key: 'extraLink', label: 'رابط إضافي', show: showExtraLinkInput, onSelect: () => setShowExtraLinkInput(true) },
              {
                key: 'whatsappGroup',
                label: 'قروب واتساب',
                show: showWhatsappGroupInput,
                onSelect: () => {
                  setShowWhatsappGroupInput(true);
                  setProfile(prev => ({
                    ...prev,
                    whatsappGroupLabel: prev.whatsappGroupLabel || 'انضم لقروب العروض'
                  }));
                }
              },
            ].filter(option => !option.show);
            const setAvailablePropertiesVisible = (visible) => {
              // This single UI switch still saves the two existing backend fields.
              setProfile({
                ...profile,
                showCatalogsSection: visible,
                showOffersSection: visible
              });
            };
            const themePreview = getThemePreviewColors(themeDraft.landingTheme, themeDraft.brandColor);
            return (
              <div className="settings-section">
                <h2>موقعي العقاري</h2>
                <p className="section-desc">رتّب صفحتك العامة كما يراها العملاء.</p>

                <div className="landing-cards">
                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">رابط موقعك</h3>
                    <p className="landing-card-desc">اختر الرابط الذي تشاركه مع العملاء.</p>
                    <div className="landing-url-box">
                      <span className="landing-url-label">رابط صفحتك</span>
                      <div className="landing-url-row">
                        <span className="landing-url-text">
                          {landingUsername
                            ? `miskest.com/p/${landingUsername}`
                            : 'أضف اسم مستخدم لإنشاء رابط موقعك العقاري.'}
                        </span>
                        <button
                          className="landing-url-copy"
                          onClick={handleCopyLandingUrl}
                          disabled={!landingUsername}
                        >
                          نسخ
                        </button>
                        <button
                          type="button"
                          className="landing-url-preview"
                          onClick={handlePreviewLandingUrl}
                          disabled={!landingUsername}
                        >
                          معاينة
                        </button>
                        <a
                          href="/preview/marketer"
                          target="_blank"
                          rel="noreferrer"
                          className="landing-url-example"
                        >
                          شاهد مثال
                        </a>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>اسم المستخدم للرابط</label>
                      <input
                        type="text"
                        value={profile.username}
                        onChange={e => setProfile({ ...profile, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="مثال: ahmed-alomari"
                        className="form-control"
                        dir="ltr"
                      />
                      <span className="form-hint">أحرف إنجليزية صغيرة وأرقام وشرطة فقط.</span>
                    </div>
                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">الهوية البصرية</h3>
                    <p className="landing-card-desc">اختر صورة الغلاف ولون الهوية.</p>
                    <div className="form-group">
                      <label>ثيم الصفحة</label>
                      <div className="landing-theme-options" role="group" aria-label="ثيم الصفحة">
                        {LANDING_THEME_OPTIONS.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            className={`landing-theme-option${themeDraft.landingTheme === option.value ? ' selected' : ''}${option.disabled ? ' disabled' : ''}`}
                            onClick={() => handleThemeSelect(option.value)}
                            disabled={option.disabled}
                          >
                            <span>{option.label}{option.disabled && <em className="soon-badge">قريبًا</em>}</span>
                            <small>{option.desc}</small>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="landing-theme-preview" style={{ background: themePreview.page, borderColor: themePreview.border }}>
                      <div className="theme-preview-top" style={{ background: themePreview.top }} />
                      <div className="theme-preview-body">
                        <div className="theme-preview-card" style={{ background: themePreview.card, borderColor: themePreview.border }}>
                          <strong style={{ color: themePreview.text }}>عنوان البطاقة</strong>
                          <span style={{ color: themePreview.muted }}>وصف مختصر للثيم المختار</span>
                          <div className="theme-preview-actions">
                            <button type="button" style={{ background: themePreview.primary }}>زر أساسي</button>
                            <button type="button" className="theme-preview-whatsapp">واتساب</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-group landing-avatar-logo-section">
                      <label>الشعار أو صورة الحساب</label>
                      <p className="form-hint">تظهر في صفحتك العامة والكتالوجات، وتُستخدم لاستخراج ألوان الهوية.</p>
                      <div className="landing-avatar-logo-row">
                        <div className="landing-avatar-logo-preview">
                          {profile.avatar ? (
                            <img src={profile.avatar} alt={profile.name || 'الشعار أو صورة الحساب'} />
                          ) : (
                            <span>{profile.name?.charAt(0) || 'م'}</span>
                          )}
                          {avatarUploading && (
                            <div className="avatar-loading">
                              <Loader className="spin" size={22} />
                            </div>
                          )}
                        </div>
                        <div className="landing-avatar-logo-actions">
                          <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleAvatarChange}
                          />
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={avatarUploading}
                          >
                            <Camera size={16} />
                            {avatarUploading ? 'جاري الرفع...' : 'رفع أو تغيير الصورة'}
                          </button>
                        </div>
                      </div>
                    </div>                    <div className="form-group">
                      <label>صورة الغلاف</label>
                      <p className="form-hint">تظهر في أعلى صفحتك العقارية فقط.</p>
                      <div className="hero-image-mode-options" role="group" aria-label="خيارات صورة الغلاف">
                        {HERO_IMAGE_MODE_OPTIONS.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            className={`hero-image-mode-option${heroImageMode === option.value ? ' selected' : ''}`}
                            onClick={() => handleHeroImageModeChange(option.value)}
                          >
                            <strong>{option.label}</strong>
                            <span>{option.desc}</span>
                          </button>
                        ))}
                      </div>
                      <div className="hero-image-preview">
                        {heroImageMode === 'default' ? (
                          <div className="hero-image-placeholder hero-image-placeholder-default">
                            استخدام صورة الغلاف الافتراضية الحالية
                          </div>
                        ) : heroImageMode === 'none' ? (
                          <div className="hero-image-placeholder hero-image-placeholder-none">
                            بدون صورة غلاف - ستظهر خلفية تدرج من الثيم
                          </div>
                        ) : (heroImagePreview || profile.heroImageUrl) ? (
                          <img
                            src={heroImagePreview || profile.heroImageUrl}
                            alt="hero"
                            style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px' }}
                          />
                        ) : (
                          <div className="hero-image-placeholder">
                            لم تختر صورة خاصة بعد
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        ref={heroFileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleHeroImageChange}
                      />
                      {heroImageMode === 'custom' && (
                        <>
                          {!heroImagePreview && !profile.heroImageUrl && (
                            <div className="landing-note hero-image-warning">
                              اختر صورة خاصة قبل الحفظ، أو استخدم الصورة الافتراضية حتى لا تظهر صفحتك بدون غلاف مخصص.
                            </div>
                          )}
                          <button className="btn btn-outline" style={{ marginTop: '10px' }} onClick={() => heroFileInputRef.current?.click()}>
                            <Camera size={16} />
                            رفع أو تغيير الصورة
                          </button>
                        </>
                      )}
                    </div>
                    {false && themeDraft.landingTheme === 'custom' && (
                      <div className="form-group landing-logo-theme-panel">
                        <label>ألوان مستخرجة من الشعار أو صورة الحساب</label>
                        <p className="form-hint">ارفع أو غيّر الصورة أعلاه، ثم اختر لونًا من الألوان المقترحة للمعاينة.</p>
                        {extractedColors.length > 0 ? (
                          <div>
                            <div className="color-swatches-row">
                              {extractedColors.map((hex, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`color-swatch${themeDraft.brandColor === hex ? ' selected' : ''}`}
                                  style={{ backgroundColor: hex }}
                                  onClick={() => handleExtractedColorSelect(hex)}
                                  title={hex}
                                />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="form-hint" style={{ marginTop: '10px' }}>ستظهر الألوان المقترحة هنا بعد رفع الشعار أو صورة الحساب.</p>
                        )}
                      </div>
                    )}                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">الخدمات</h3>
                    <p className="landing-card-desc">اختر الخدمات التي تريد إظهارها في صفحتك العقارية.</p>
                    <div className="form-group">
                      <label>اختر من الخدمات الجاهزة</label>
                      <button
                        type="button"
                        className={`landing-dropdown-trigger${serviceDropdownOpen ? ' open' : ''}`}
                        onClick={() => setServiceDropdownOpen(!serviceDropdownOpen)}
                      >
                        <span>{services.length > 0 ? `${services.length} خدمات مختارة` : 'اختر الخدمات'}</span>
                        <ChevronDown size={18} />
                      </button>
                      {serviceDropdownOpen && (
                        <div className="landing-dropdown-panel">
                          <div className="landing-option-list">
                            {PREDEFINED_SERVICES.map(svc => {
                              const selected = services.includes(svc);
                              return (
                                <button
                                  key={svc}
                                  type="button"
                                  className={`landing-option-row${selected ? ' selected' : ''}`}
                                  onClick={() => handleServiceToggle(svc)}
                                >
                                  <span>{svc}</span>
                                  {selected && <Check size={15} />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <span className="form-hint">يمكنك اختيار أكثر من خدمة، وستظهر المختارات كوسوم بالأسفل.</span>
                    </div>
                    <div className="form-group">
                      <div className="landing-field-title-row">
                        <label>الخدمات المختارة</label>
                        <button type="button" className="landing-reset-btn" onClick={handleResetServices}>
                          إعادة تعيين
                        </button>
                      </div>
                      <div className="landing-selected-services">
                        {services.length === 0 ? (
                          <span className="form-hint">لم تختر أي خدمة بعد.</span>
                        ) : (
                          services.map((svc, i) => (
                            <span key={`${svc}-${i}`} className="landing-selected-service">
                              {svc}
                              <button
                                type="button"
                                onClick={() => setServices(services.filter((_, idx) => idx !== i))}
                                aria-label="حذف الخدمة"
                              >
                                <X size={13} />
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>إضافة خدمة مخصصة</label>
                      <div className="landing-inline-action">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="مثال: تسويق مشاريع سكنية"
                          value={newService}
                          onChange={e => setNewService(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newService.trim()) {
                              if (!services.includes(newService.trim())) {
                                setServices([...services, newService.trim()]);
                              }
                              setNewService('');
                            }
                          }}
                        />
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={() => {
                            if (newService.trim()) {
                              if (!services.includes(newService.trim())) {
                                setServices([...services, newService.trim()]);
                              }
                              setNewService('');
                            }
                          }}
                        >
                          إضافة
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">بيانات التعريف</h3>
                    <p className="landing-card-desc">معلومات مختصرة تساعد العميل يعرف خبرتك.</p>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>المسمى الظاهر أعلى الصفحة</label>
                        <input
                          type="text"
                          value={profile.specialty}
                          onChange={e => setProfile({ ...profile, specialty: e.target.value })}
                          placeholder="مثال: مستشار عقاري معتمد"
                          className="form-control"
                        />
                        <span className="form-hint">تظهر المدينة من بيانات الملف الشخصي.</span>
                      </div>
                      <div className="form-group full-width">
                        <label>رسالة ترحيبية قصيرة</label>
                        <input
                          type="text"
                          value={profile.heroIntroText}
                          onChange={e => setProfile({ ...profile, heroIntroText: e.target.value.slice(0, 90) })}
                          placeholder="مثال: أساعدك تجد العقار الأنسب بثقة وسرعة"
                          className="form-control"
                          maxLength={90}
                        />
                        <div className="field-hint-row">
                          <span className="form-hint">تظهر كسطر مختصر في الغلاف. اكتب جملة قصيرة لا تتجاوز 90 حرفًا.</span>
                          <span className="char-counter">{(profile.heroIntroText || '').length}/90</span>
                        </div>
                      </div>
                      <div className="form-group full-width">
                        <label>نبذة عنك</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={profile.bio}
                          onChange={e => setProfile({ ...profile, bio: e.target.value })}
                          placeholder="اكتب نبذة تظهر في قسم من أنا داخل موقعك العقاري"
                        />
                        <span className="form-hint">تظهر هذه النبذة في موقعك العقاري إذا كان قسم من أنا مفعّلًا.</span>
                      </div>
                      <div className="form-group">
                        <label>سنوات الخبرة</label>
                        <input
                          type="number"
                          value={profile.experience}
                          onChange={e => setProfile({ ...profile, experience: e.target.value })}
                          placeholder="7"
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>الصفقات المنجزة</label>
                        <input
                          type="number"
                          value={profile.dealsDone}
                          onChange={e => setProfile({ ...profile, dealsDone: e.target.value })}
                          placeholder="120"
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>رخصة فال</label>
                        <input
                          type="text"
                          value={profile.falLicense}
                          onChange={e => setProfile({ ...profile, falLicense: e.target.value })}
                          placeholder="FAL-12345678"
                          className="form-control"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">نطاق العمل</h3>
                    <p className="landing-card-desc">وضح للعميل المناطق والأسعار التي تعمل عليها.</p>
                    <div className="form-group">
                      <label>مناطق العمل</label>
                      <button
                        type="button"
                        className={`landing-dropdown-trigger${cityDropdownOpen ? ' open' : ''}`}
                        onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
                        disabled={loadingLocations}
                      >
                        <span>{selectedDistrictCities.length > 0 ? `${selectedDistrictCities.length} مدن مختارة` : 'اختر المدن'}</span>
                        <ChevronDown size={18} />
                      </button>
                      {cityDropdownOpen && (
                        <div className="landing-dropdown-panel">
                          <input
                            type="text"
                            className="form-control landing-dropdown-search"
                            value={citySearch}
                            onChange={e => setCitySearch(e.target.value)}
                            placeholder="ابحث عن مدينة..."
                          />
                          <div className="landing-option-list">
                            {filteredCityOptions.length === 0 ? (
                              <span className="landing-empty-option">لا توجد مدينة مطابقة</span>
                            ) : (
                              filteredCityOptions.map(cityName => {
                                const selected = selectedDistrictCities.includes(cityName);
                                return (
                                  <button
                                    key={cityName}
                                    type="button"
                                    className={`landing-option-row${selected ? ' selected' : ''}`}
                                    onClick={() => handleDistrictCityToggle(cityName)}
                                  >
                                    <span>{cityName}</span>
                                    {selected && <Check size={15} />}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                      <span className="form-hint">اختر المدن فقط الآن. الأحياء ستكون في مرحلة لاحقة.</span>
                      <div className="landing-selected-services landing-city-chips">
                        {selectedDistrictCities.length === 0 ? (
                          <span className="form-hint">لم تختر أي مدينة بعد.</span>
                        ) : (
                          selectedDistrictCities.map(cityName => (
                            <span key={cityName} className="landing-selected-service">
                              {cityName}
                              <button
                                type="button"
                                onClick={() => handleDistrictCityToggle(cityName)}
                                aria-label="حذف المدينة"
                              >
                                <X size={13} />
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>الحد الأدنى للسعر</label>
                        <input
                          type="number"
                          value={profile.priceRangeMin}
                          onChange={e => setProfile({ ...profile, priceRangeMin: e.target.value })}
                          placeholder="500000"
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>الحد الأعلى للسعر</label>
                        <input
                          type="number"
                          value={profile.priceRangeMax}
                          onChange={e => setProfile({ ...profile, priceRangeMax: e.target.value })}
                          placeholder="5000000"
                          className="form-control"
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>اللغات</label>
                      <input
                        type="text"
                        value={profile.languages}
                        onChange={e => setProfile({ ...profile, languages: e.target.value })}
                        placeholder="عربي · إنجليزي"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">التواصل</h3>
                    <p className="landing-card-desc">هذه البيانات عامة ومستقلة عن بيانات الحساب. اترك أي حقل فارغًا لإخفائه.</p>
                    <div className="landing-three-col public-contact-fields">
                      <div className="form-group"><label>رقم الاتصال العام</label><input className="form-control" dir="ltr" value={profile.publicContactPhone} onChange={e => setProfile({ ...profile, publicContactPhone: e.target.value })} /></div>
                      <div className="form-group"><label>رقم واتساب العام</label><input className="form-control" dir="ltr" value={profile.publicWhatsappPhone} onChange={e => setProfile({ ...profile, publicWhatsappPhone: e.target.value })} /></div>
                      <div className="form-group"><label>البريد الإلكتروني العام</label><input className="form-control" dir="ltr" type="email" value={profile.publicContactEmail} onChange={e => setProfile({ ...profile, publicContactEmail: e.target.value })} /></div>
                    </div>
                    <div className="landing-add-social-actions">
                      <button
                        type="button"
                        className={`landing-dropdown-trigger${contactDropdownOpen ? ' open' : ''}`}
                        onClick={() => setContactDropdownOpen(!contactDropdownOpen)}
                        disabled={availableContactOptions.length === 0}
                      >
                        <span>{availableContactOptions.length === 0 ? 'تمت إضافة كل الوسائل' : 'إضافة وسيلة تواصل'}</span>
                        <ChevronDown size={18} />
                      </button>
                      {contactDropdownOpen && availableContactOptions.length > 0 && (
                        <div className="landing-dropdown-panel">
                          <div className="landing-option-list">
                            {availableContactOptions.map(option => (
                              <button
                                key={option.key}
                                type="button"
                                className="landing-option-row"
                                onClick={() => {
                                  option.onSelect();
                                  setContactDropdownOpen(false);
                                }}
                              >
                                <span>{option.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {showInstagramInput && (
                      <div className="form-group landing-optional-field">
                        <div className="landing-field-title-row">
                          <label>Instagram</label>
                          <button
                            type="button"
                            className="landing-remove-field-btn"
                            onClick={() => {
                              setProfile({ ...profile, instagram: '' });
                              setShowInstagramInput(false);
                            }}
                          >
                            حذف
                          </button>
                        </div>
                        <input
                          type="text"
                          value={profile.instagram}
                          onChange={e => setProfile({ ...profile, instagram: e.target.value })}
                          placeholder="https://instagram.com/username"
                          className="form-control"
                          dir="ltr"
                        />
                      </div>
                    )}
                    {showTiktokInput && (
                      <div className="form-group landing-optional-field">
                        <div className="landing-field-title-row">
                          <label>TikTok</label>
                          <button
                            type="button"
                            className="landing-remove-field-btn"
                            onClick={() => {
                              setProfile({ ...profile, tiktok: '' });
                              setShowTiktokInput(false);
                            }}
                          >
                            حذف
                          </button>
                        </div>
                        <input
                          type="text"
                          value={profile.tiktok}
                          onChange={e => setProfile({ ...profile, tiktok: e.target.value })}
                          placeholder="https://tiktok.com/@username"
                          className="form-control"
                          dir="ltr"
                        />
                      </div>
                    )}
                    {showTwitterInput && (
                      <div className="form-group landing-optional-field">
                        <div className="landing-field-title-row">
                          <label>إكس</label>
                          <button
                            type="button"
                            className="landing-remove-field-btn"
                            onClick={() => {
                              setProfile({ ...profile, twitter: '' });
                              setShowTwitterInput(false);
                            }}
                          >
                            حذف
                          </button>
                        </div>
                        <input
                          type="text"
                          value={profile.twitter}
                          onChange={e => setProfile({ ...profile, twitter: e.target.value })}
                          placeholder="@username"
                          className="form-control"
                          dir="ltr"
                        />
                      </div>
                    )}
                    {showExtraLinkInput && (
                      <div className="form-group landing-optional-field">
                        <div className="landing-field-title-row">
                          <label>رابط إضافي</label>
                          <button
                            type="button"
                            className="landing-remove-field-btn"
                            onClick={() => {
                              setProfile({ ...profile, extraLink: '' });
                              setShowExtraLinkInput(false);
                            }}
                          >
                            حذف
                          </button>
                        </div>
                        <input
                          type="text"
                          value={profile.extraLink}
                          onChange={e => setProfile({ ...profile, extraLink: e.target.value })}
                          placeholder="https://example.com"
                          className="form-control"
                          dir="ltr"
                        />
                      </div>
                    )}
                    {showWhatsappGroupInput && (
                      <div className="form-group landing-optional-field">
                        <div className="landing-field-title-row">
                          <label>قروب واتساب</label>
                          <button
                            type="button"
                            className="landing-remove-field-btn"
                            onClick={() => {
                              setProfile({ ...profile, whatsappGroupUrl: '', whatsappGroupLabel: '' });
                              setShowWhatsappGroupInput(false);
                            }}
                          >
                            حذف
                          </button>
                        </div>
                        <div className="landing-two-col">
                          <input
                            type="text"
                            value={profile.whatsappGroupLabel}
                            onChange={e => setProfile({ ...profile, whatsappGroupLabel: e.target.value })}
                            placeholder="انضم لقروب العروض"
                            className="form-control"
                          />
                          <input
                            type="text"
                            value={profile.whatsappGroupUrl}
                            onChange={e => setProfile({ ...profile, whatsappGroupUrl: e.target.value })}
                            placeholder="https://chat.whatsapp.com/..."
                            className="form-control"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    )}
                    <div className="landing-additional-contacts">
                      <div className="landing-field-title-row">
                        <label>أرقام تواصل إضافية</label>
                        <button
                          type="button"
                          className="landing-add-contact-btn"
                          onClick={handleAddAdditionalContact}
                        >
                          إضافة رقم تواصل
                        </button>
                      </div>
                      {(profile.additionalContactNumbers || []).map((contact, index) => (
                        <div className="landing-additional-contact-card" key={`additional-contact-${index}`}>
                          <div className="landing-two-col">
                            <input
                              type="text"
                              value={contact.label || ''}
                              onChange={e => handleUpdateAdditionalContact(index, 'label', e.target.value)}
                              placeholder="الاسم / القسم"
                              className="form-control"
                            />
                            <input
                              type="text"
                              value={contact.phone || ''}
                              onChange={e => handleUpdateAdditionalContact(index, 'phone', e.target.value)}
                              placeholder="05xxxxxxxx"
                              className="form-control"
                              dir="ltr"
                            />
                          </div>
                          <div className="landing-contact-checkboxes">
                            {[
                              { key: 'whatsappEnabled', label: 'واتساب', defaultChecked: true },
                              { key: 'callEnabled', label: 'اتصال', defaultChecked: false },
                              { key: 'showInPublicSite', label: 'إظهاره في الموقع العقاري', defaultChecked: true },
                              { key: 'showInCatalog', label: 'إظهاره في الكتالوج', defaultChecked: false },
                            ].map(option => (
                              <label key={option.key} className="landing-contact-checkbox">
                                <input
                                  type="checkbox"
                                  checked={option.defaultChecked ? contact[option.key] !== false : contact[option.key] === true}
                                  onChange={e => handleUpdateAdditionalContact(index, option.key, e.target.checked)}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="landing-remove-field-btn landing-contact-remove"
                            onClick={() => handleRemoveAdditionalContact(index)}
                          >
                            حذف الرقم
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="landing-subsection landing-card">
                    <h3 className="landing-subsection-title">الأقسام الظاهرة</h3>
                    <p className="landing-card-desc">اختر ما يظهر في موقعك العقاري.</p>
                    <div className="sections-toggles">
                      <div className="section-toggle-row section-toggle-row--stacked">
                        <div>
                          <span className="section-toggle-label">إظهار بيانات الثقة في الهيرو</span>
                          <p className="section-toggle-desc">يعرض رخصة فال، سنوات الخبرة، وعدد الصفقات أسفل اسمك في الغلاف عند توفرها.</p>
                        </div>
                        <button
                          type="button"
                          className={`section-toggle-btn${profile.showHeroTrustLine ? ' active' : ''}`}
                          onClick={() => setProfile({ ...profile, showHeroTrustLine: !profile.showHeroTrustLine })}
                        >
                          {profile.showHeroTrustLine ? 'ظاهر' : 'مخفي'}
                        </button>
                      </div>
                      {[
                        { key: 'showAboutSection', label: 'التعريف' },
                        { key: 'showStatsSection', label: 'الإحصائيات' },
                        { key: 'showSpecialtySection', label: 'نطاق العمل' },
                        { key: 'showContactSection', label: 'التواصل' },
                      ].map(({ key, label }) => (
                        <div key={key} className="section-toggle-row">
                          <span className="section-toggle-label">{label}</span>
                          <button
                            type="button"
                            className={`section-toggle-btn${profile[key] ? ' active' : ''}`}
                            onClick={() => setProfile({ ...profile, [key]: !profile[key] })}
                          >
                            {profile[key] ? 'ظاهر' : 'مخفي'}
                          </button>
                        </div>
                      ))}
                      <div className="section-toggle-row">
                        <span className="section-toggle-label">العقارات المتاحة</span>
                        <button
                          type="button"
                          className={`section-toggle-btn${availablePropertiesVisible ? ' active' : ''}`}
                          onClick={() => setAvailablePropertiesVisible(!availablePropertiesVisible)}
                        >
                          {availablePropertiesVisible ? 'ظاهر' : 'مخفي'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSaveLanding} disabled={saving}>
                    {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                    حفظ التغييرات
                  </button>
                  {saveSuccess && activeTab === 'landing' && (
                    <span className="save-success">
                      <Check size={18} />
                      تم الحفظ بنجاح
                    </span>
                  )}
                  {error && activeTab === 'landing' && (
                    <span className="error-message">
                      <AlertCircle size={18} />
                      {error}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
          {activeTab === 'other' && (
              <div className="settings-section">
                <h2 className="section-title">سلة المحذوفات</h2>
                {deletedOffersLoading ? (
                  <div className="loading-state"><Loader size={24} className="spin" /> جار التحميل...</div>
                ) : deletedOffers.length === 0 ? (
                  <p className="empty-state">سلة المحذوفات فارغة 🗑️</p>
                ) : (
                  <div className="deleted-offers-list">
                    {deletedOffers.map(offer => (
                      <div key={offer._id} className="deleted-offer-row">
                        <div className="deleted-offer-info">
                          <span className="deleted-offer-title">{offer.title}</span>
                          {offer.city && <span className="deleted-offer-city">{offer.city}</span>}
                        </div>
                        <div className="deleted-offer-actions">
                          <span className="badge-days-remaining">متبقي {offer.daysRemaining} يوم</span>
                          <button
                            className="btn-restore"
                            onClick={() => handleRestoreOffer(offer._id)}
                          >
                            استعادة
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          )}

          {activeTab === 'team' && !isSubAccount && (
            <div className="settings-section" style={{ padding: 0 }}>
              <TeamPage embedded />
            </div>
          )}

        </div>  {/* settings-content */}
      </div>  {/* settings-container */}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              <AlertCircle size={24} color="#ef4444" />
              تأكيد حذف الحساب
            </h3>
            <p>هل أنت متأكد من رغبتك في حذف حسابك؟ سيتم حذف جميع بياناتك بشكل نهائي ولا يمكن استرجاعها.</p>
            
            <div className="form-group">
              <label>أدخل كلمة المرور للتأكيد</label>
              <input
                type="password"
                className="form-control"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="كلمة المرور"
              />
            </div>

            {error && (
              <div className="error-banner">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setError(null);
              }}>
                إلغاء
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? <Loader className="spin" size={18} /> : <Trash2 size={18} />}
                حذف الحساب نهائياً
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PermissionGate>
  );
};

export default SettingsPage;
